import { useState, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import Avatar from '@components/ui/Avatar/Avatar';
import Tabs from '@components/ui/Tabs/Tabs';
import type { TabItem } from '@components/ui/Tabs/Tabs';
import DataTable from '@components/shared/DataTable/DataTable';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { TenantModal } from '@/features/tenants/CreateTenantModal';
import { getTenants, activateTenant, deleteTenant, getProductTypes, getTenantCounts } from '@/services/tenantService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import { useCountries } from '@/hooks/useCountries';
import type { TenantDto, TenantWithUser } from '@/types/tenant';
import type { FilterFieldConfig } from '@/types/filters';
import { WorkpayApiError } from '@/lib/apiEnvelope';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState, serializeFilters } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import { useTranslation } from '@/hooks/useTranslation';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { useAuditTrigger } from '@/hooks/useAuditTrigger';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/dashboard/')({
  validateSearch: (search: Record<string, unknown>) =>
    validateTableSearch(search, { sortBy: 'created', sortDir: 'desc', tab: 'ACTIVE' }),
  component: DashboardPage,
});

const TENANT_STATUS_CLASS: Record<string, string> = {
  ACTIVE: 'badgeActive',
  ONBOARDING: 'badgeInfo',
  CONTRACT_REVIEW_PENDING: 'badgeInfo',
  ACTIVATION_PENDING: 'badgeInfo',
  INACTIVE: 'badgeInactive',
};

const TENANT_STATUS_KEY: Record<string, [string, string]> = {
  CONTRACT_REVIEW_PENDING: ['K_STATUS_CONTRACT_REVIEW_PENDING', 'Contract Review'],
  ACTIVATION_PENDING: ['K_STATUS_ACTIVATION_PENDING', 'Activation Pending'],
};

function StatusBadge({ status }: { status: TenantDto['status'] }) {
  const { t } = useTranslation();
  const className = styles[TENANT_STATUS_CLASS[status] ?? 'badgeInactive'] ?? '';
  const keyEntry = TENANT_STATUS_KEY[status];
  const label = keyEntry ? t(keyEntry[0], keyEntry[1]) : toTitleCase(status);
  return <span className={`${styles.badge} ${className}`}>{label}</span>;
}

function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam, tab } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;
  const activeTab = tab ?? 'ACTIVE';

  const { data: countsData } = useQuery({
    queryKey: ['tenantCounts'],
    queryFn: getTenantCounts,
  });

  const tabCounts = useMemo(() => {
    if (!countsData) return undefined;
    const total =
      (countsData.ACTIVE ?? 0) +
      (countsData.ONBOARDING ?? 0) +
      (countsData.CONTRACT_REVIEW_PENDING ?? 0) +
      (countsData.ACTIVATION_PENDING ?? 0) +
      (countsData.INACTIVE ?? 0);
    return {
      ACTIVE: countsData.ACTIVE ?? 0,
      ONBOARDING: countsData.ONBOARDING ?? 0,
      CONTRACT_REVIEW_PENDING: countsData.CONTRACT_REVIEW_PENDING ?? 0,
      ACTIVATION_PENDING: countsData.ACTIVATION_PENDING ?? 0,
      INACTIVE: countsData.INACTIVE ?? 0,
      ALL: total,
    };
  }, [countsData]);

  const statusTabs = useMemo<TabItem[]>(
    () => [
      { id: 'ACTIVE', label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')) },
      { id: 'ONBOARDING', label: toTitleCase(t('K_STATUS_ONBOARDING', 'Onboarding')) },
      { id: 'CONTRACT_REVIEW_PENDING', label: t('K_STATUS_CONTRACT_REVIEW_PENDING', 'Contract Review') },
      { id: 'ACTIVATION_PENDING', label: toTitleCase(t('K_STATUS_ACTIVATION_PENDING', 'Activation Pending')) },
      { id: 'INACTIVE', label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')) },
      { id: 'ALL', label: t('K_STATUS_ALL', 'All') },
    ],
    [t],
  );

  const handleTabChange = useCallback(
    (tabId: string) => {
      void navigate({
        to: '/w/dashboard',
        search: {
          tab: tabId,
          page: 0,
          size: urlSize,
          sortBy: 'created',
          sortDir: 'desc' as const,
        },
        replace: true,
      });
    },
    [navigate, urlSize],
  );

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  const { countries } = useCountries();

  const { data: productTypesData } = useQuery({
    queryKey: ['productTypes', { size: 100 }],
    queryFn: () => getProductTypes({ size: 100 }),
  });
  const productTypeOptions = useMemo(
    () => (productTypesData?.content ?? []).map((pt) => ({ label: pt.productType, value: pt.id })),
    [productTypesData],
  );

  // Filter fields ordered to match table columns:
  // Organisation, Subdomain, Country, Email, Org Type, Products, Status, Created
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'organization.organizationName',
        label: t('K_FILTER_ORG_NAME', 'Company Name'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_ORG_NAME', 'Search company name...'),
      },
      {
        fieldKey: 'subdomain',
        label: t('K_FILTER_SUBDOMAIN', 'Subdomain'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_SUBDOMAIN', 'Search subdomain...'),
      },
      {
        fieldKey: 'country.id',
        label: t('K_FILTER_COUNTRY', 'Country'),
        type: 'select',
        options: countries.map((c) => ({ label: c.name, value: c.id })),
      },
      {
        fieldKey: 'email',
        label: t('K_FILTER_EMAIL', 'Email'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_EMAIL', 'Search email...'),
      },
      {
        fieldKey: 'organization.organizationSubType',
        label: t('K_FILTER_ORG_TYPE', 'Company Type'),
        type: 'select',
        options: [
          { label: t('K_OPTION_STANDARD_ACCOUNT', 'Standard Account'), value: 'STANDARD_ACCOUNT' },
          { label: t('K_OPTION_GROUP_ACCOUNT', 'Group Account'), value: 'GROUP_ACCOUNT' },
          { label: t('K_OPTION_PARTNER_ACCOUNT', 'Partner Account'), value: 'PARTNER_ACCOUNT' },
        ],
      },
      {
        fieldKey: 'productTypeIds',
        label: t('K_FILTER_PRODUCTS', 'Products'),
        type: 'multi-select',
        options: productTypeOptions,
        placeholder: t('K_PLACEHOLDER_SELECT_PRODUCTS', 'Select products...'),
      },
      {
        fieldKey: 'createdAt',
        label: t('K_FILTER_CREATED', 'Created Date'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
    ],
    [t, countries, productTypeOptions],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/w/dashboard',
    filtersParam,
    fields: filterFields,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activateItem, setActivateItem] = useState<TenantWithUser | null>(null);
  const [deactivateItem, setDeactivateItem] = useState<TenantWithUser | null>(null);
  const audit = useAuditTrigger();
  const auditOpen = audit.open;

  const handleCreate = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const activateMutation = useMutation({
    mutationFn: (id: string) => activateTenant(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenantCounts'] });
      setActivateItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setActivateItem(null);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deleteTenant(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenantCounts'] });
      setDeactivateItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setDeactivateItem(null);
    },
  });

  const columns = useMemo<DataTableColumn<TenantWithUser>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_ORGANISATION', 'Company'),
        sortable: true,
        sortKey: 'name',
        width: '240px',
        cell: (item) => {
          const name = item.tenant.name || '-';
          return (
            <span className={styles.nameCell} title={name}>
              <Avatar
                name={name}
                size="xs"
                color="green"
                {...(item.organization?.profileImageUrl ? { src: item.organization.profileImageUrl } : {})}
              />
              <span className={styles.cellPrimary}>{name}</span>
            </span>
          );
        },
      },
      {
        id: 'subdomain',
        header: t('K_COLUMN_SUBDOMAIN', 'Subdomain'),
        sortable: true,
        sortKey: 'subdomain',
        cell: (item) =>
          item.tenant.subdomain ? `${item.tenant.subdomain}.workpay.com` : '-',
      },
      {
        id: 'country',
        header: t('K_COLUMN_COUNTRY', 'Country'),
        sortable: true,
        sortKey: 'country',
        cell: (item) => item.tenant.country?.name || '-',
      },
      {
        id: 'email',
        header: t('K_COLUMN_EMAIL', 'Email'),
        cell: (item) => item.user?.email || '-',
      },
      {
        id: 'orgType',
        header: t('K_COLUMN_ORGANISATION_TYPE', 'Company Type'),
        cell: (item) => {
          const subType = item.organization?.organizationSubType;
          if (subType === 'STANDARD_ACCOUNT') return t('K_OPTION_STANDARD_ACCOUNT', 'Standard Account');
          if (subType === 'GROUP_ACCOUNT') return t('K_OPTION_GROUP_ACCOUNT', 'Group Account');
          if (subType === 'PARTNER_ACCOUNT') return t('K_OPTION_PARTNER_ACCOUNT', 'Partner Account');
          return '-';
        },
      },
      {
        id: 'products',
        header: t('K_COLUMN_PRODUCTS', 'Products'),
        cell: (item) =>
          item.tenant.productTypes?.map((pt) => pt.productType).join(', ') || '-',
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.tenant.status} />,
      },
      {
        id: 'created',
        header: t('K_COLUMN_CREATED', 'Created'),
        sortable: true,
        sortKey: 'createdAt',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.tenant.createdAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'updated',
        header: t('K_COLUMN_UPDATED', 'Updated'),
        sortable: true,
        sortKey: 'updatedAt',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.tenant.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [t],
  );

  const rowActions = useCallback((item: TenantWithUser) => {
    const actions: ProfileAction[] = [];
    if (hasPermission('TENANTS_UPDATE') && item.tenant.status !== 'INACTIVE') {
      actions.push({
        id: 'edit',
        label: t('K_EDIT', 'Edit'),
        onClick: () =>
          navigate({
            to: '/w/dashboard/profile/$tenantId',
            params: { tenantId: item.tenant.id },
            search: { edit: true },
          }),
      });
    }
    const canActivate = hasPermission('TENANTS_ACTIVATE') &&
      (item.tenant.status === 'ONBOARDING' || item.tenant.status === 'ACTIVATION_PENDING');
    if (canActivate) {
      actions.push({ id: 'activate', label: t('K_BUTTON_ACTIVATE', 'Activate'), onClick: () => setActivateItem(item) });
    }
    actions.push({
      id: 'viewEmployees',
      label: t('K_BUTTON_VIEW_ALL_EMPLOYEES', 'View All Employees'),
      onClick: () =>
        navigate({
          to: '/w/employees',
          search: {
            tab: 'ALL',
            filters: serializeFilters({ tenantId: item.tenant.id }),
          },
        }),
    });
    const canDeactivate = hasPermission('TENANTS_DELETE') && item.tenant.status === 'ACTIVE';
    if (canDeactivate) {
      actions.push({ id: 'deactivate', label: t('K_DEACTIVATE', 'Deactivate'), onClick: () => setDeactivateItem(item) });
    }
    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => auditOpen(item.tenant.id, item.tenant.name || undefined),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, hasPermission, navigate, auditOpen]);

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/dashboard',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/dashboard',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/dashboard',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const mergedPayload = useMemo(() => {
    const baseFilters = (filterPayload?.filters ?? {}) as Record<string, unknown>;
    if (activeTab === 'ALL') return filterPayload;
    return {
      ...filterPayload,
      filters: { ...baseFilters, status: activeTab },
    };
  }, [filterPayload, activeTab]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['tenants', { page, size, sort: sortString, filterPayload: mergedPayload }],
    queryFn: () => getTenants({ page, size, sort: sortString }, mergedPayload),
  });

  const tenants = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: TenantWithUser) => item.tenant.id, []);

  const handleRowClick = useCallback(
    (item: TenantWithUser) => {
      void navigate({ to: '/w/dashboard/profile/$tenantId', params: { tenantId: item.tenant.id } });
    },
    [navigate],
  );

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_TENANTS', 'No clients yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_TENANT', 'Get started by creating your first client company.')}
          </p>
          {hasPermission('TENANTS_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_TENANT', 'Create a client')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_TENANTS', 'Clients')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_TENANTS', 'Manage all your client companies')}</p>
        </div>

        {hasPermission('TENANTS_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_TENANT', 'Create a client')}
          </Button>
        )}
      </div>

      <Tabs
        tabs={statusTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={tabCounts}
      />

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={tenants}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        loadingText={t('K_LOADING_TENANTS', 'Loading clients...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Tenants list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <TenantModal isOpen={isModalOpen} onClose={handleModalClose} editData={null} />

      <AlertModal
        variant="warning"
        title={t('K_MODAL_ACTIVATE_TENANT', 'Activate Client')}
        description={t('K_CONFIRM_ACTIVATE_TENANT', 'Are you sure you want to activate "{name}"? This will change the client status from Onboarding to Active.').replace('{name}', activateItem?.tenant.name ?? '')}
        isOpen={activateItem !== null}
        onClose={() => setActivateItem(null)}
        primaryLabel={t('K_BUTTON_ACTIVATE', 'Activate')}
        onPrimaryClick={() => {
          if (activateItem) activateMutation.mutate(activateItem.tenant.id);
        }}
        loading={activateMutation.isPending}
      />

      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_TENANT', 'Deactivate Client')}
        description={t('K_CONFIRM_DEACTIVATE_TENANT', 'Are you sure you want to deactivate "{name}"? The client will no longer be active.').replace('{name}', deactivateItem?.tenant.name ?? '')}
        isOpen={deactivateItem !== null}
        onClose={() => setDeactivateItem(null)}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={() => {
          if (deactivateItem) deactivateMutation.mutate(deactivateItem.tenant.id);
        }}
        loading={deactivateMutation.isPending}
      />

      <AuditTrailModal
        open={audit.target !== null}
        onClose={audit.close}
        filter={{ resourceType: AUDIT_RESOURCE.CLIENT, resourceId: audit.target?.id ?? '' }}
        subject={audit.target?.name}
      />
    </div>
  );
}
